import React, { useContext, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { App, Button, Table, Tooltip, Switch } from 'antd'
import { PageHeader } from '../../design-system/PageHeader'
import { WarningTwoTone, DeleteOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { track } from '../../services/analytics/analytics'
import { getAutomations } from '../../graphql/custom-queries'
import { notificationStore } from '../../context/notificationsContext/store'

import IntlMessages from '../../util/IntlMessages'
import CircularProgress from '../circular-progress'
import HandleSubscribeAutomations from '../handle-subscribe-automations'
import FormattedDate from '@vacationtracker/shared/components/formatted-date'
import EmptyData from '../empty-data'

import { IAutomations, IGetAutomations } from '../../types/custom-queries'
import { ITableSort } from '../../types/data'
import { FrontendUrls } from '../../types/urls'
import { IAutomationViewPage } from '@vacationtracker/shared/types/automations'
import { AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import { ColumnType } from 'antd/lib/table'

const LeaveDurationLimitView = ({ formatMessage, amIAdmin, deleteAutomation, authUserId, dateFormat, handleSubscribe, status, addonDetails, currentPlan }: IAutomationViewPage) => {
  const history = useHistory()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const { message, modal, notification } = App.useApp()

  const [isLoading, setIsLoading] = useState(true)
  const [activeAutomationStatus, setActiveAutomationStatus] = useState<{ id: string; isActive: boolean } | null>(null)
  const [sortedInfo, setSortedInfo] = useState<ITableSort>({
    columnKey: 'name',
    order: 'ascend',
  })
  const [automations, setAutomations] = useState<IAutomations[]>([])
  const [correlationId, setCorrelationId] = useState<string | null>()
  const [deleteAutomationId, setDeleteAutomationId] = useState<string | null>(null)

  const [getAutomationsQuery] = useManualQuery<IGetAutomations>(getAutomations)

  useEffect(() => {
    fetchData()
    if (correlationId && !actionNotifications.includes(correlationId) && activeAutomationStatus) {
      setActiveAutomationStatus(null)
      setCorrelationId(null)
      setDeleteAutomationId(null)
    }
  }, [actionNotifications])

  const fetchData = async () => {
    const response = await getAutomationsQuery()
    if (!response.data || response.error) throw response.error
    setAutomations(response.data.getAutomations.filter((automation) => automation.automationType === 'LEAVE_DURATION_LIMIT'))

    setIsLoading(false)
  }

  const createAutomation = () => {
    history.push(`${FrontendUrls.addons}/leave-duration-limit/create`)
  }

  const showConfirmDelete = (row: IAutomations, event) => {
    event.stopPropagation()

    modal.confirm({
      title: formatMessage({ id: 'notifications.deleteAutomationTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'notifications.deleteAutomationConfirm' }, { name: row.name, strong: (...chunks) => <strong>{chunks}</strong> }),
      okText: formatMessage({ id: 'app.delete' }),
      okType: 'danger',
      width: 500,
      maskClosable: true,
      onOk() {
        setDeleteAutomationId(row.id)
        deleteAutomation(row.id as string, row.name as string, row.automationType as string)
      },
    })
  }

  const updateAutomations = async (row: IAutomations, status: boolean) => {
    setActiveAutomationStatus({
      id: row.id,
      isActive: status,
    })
    track('AUTOMATION_TOGGLE_CHANGED', {
      automationType: row.automationType,
      automationStatus: status ? 'enabled' : 'disabled',
    })

    const response = await Api.post('/core/event', {
      eventType: 'LEAVE_DURATION_LIMIT_UPDATED',
      eventGroup: 'AUTOMATION',
      isActive: status,
      name: row.name,
      automationId: row.id,
      period: row.period,
      minimumDuration: row.minimumDuration,
      maximumDuration: row.maximumDuration,
      units: row.units,
      leaveTypeIds: row.leaveTypeIds,
      locationIds: row.locationIds,
      teamIds: row.teamIds,
      labelIds: row.labelIds,
      autoRejectResponse: row.autoRejectResponse,
      recurring: row.recurring,
    })
    setCorrelationId(response.correlationId as string)

    notification.open({
      key: response.correlationId,
      message: formatMessage({ id: 'automations.leaveDurationLimit.updateInProgress' }),
      icon: <LoadingOutlined />,
      duration: 0,
    })
    setActionNotifications([...actionNotifications, response.correlationId])
  }

  const confirmUpdateAutomations = (row: IAutomations, checked: boolean) => {
    if (!checked) {
      modal.confirm({
        title: formatMessage({ id: 'notifications.updateAutomationTitle' }),
        icon: <ExclamationCircleOutlined />,
        content: formatMessage({ id: 'notifications.updateAutomationConfirm' }),
        okText: formatMessage({ id: 'app.ok' }),
        okType: 'danger',
        width: 500,
        maskClosable: true,
        onOk() {
          updateAutomations(row, checked)
        },
      })
    } else {
      updateAutomations(row, checked)
    }
  }

  const handleTableChange = (pagination, filters, sorter: ITableSort) => {
    setSortedInfo(sorter)
  }

  const automationsColumns: ColumnType<IAutomations>[] = [
    {
      title: <IntlMessages id="app.active" />,
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      sorter: (a, b) => Number(b.isActive) - Number(a.isActive),
      sortOrder: (sortedInfo.columnKey === 'isActive' && sortedInfo.order) || null,
      render: (isActive: boolean, row: IAutomations) => {
        if (row.error) {
          return (
            <Tooltip title={<IntlMessages id={row.error} />}>
              <div style={{ width: '100%', height: '100', textAlign: 'center' }}>
                <WarningTwoTone twoToneColor="red" style={{ fontSize: 20 }} />
              </div>
            </Tooltip>
          )
        }
        return (
          <Switch
            checked={isActive}
            loading={Boolean(activeAutomationStatus && activeAutomationStatus.id === row.id)}
            onClick={(checked, event) => {
              event.stopPropagation()
              confirmUpdateAutomations(row, checked)
            }}
            disabled={(!amIAdmin && authUserId !== row.owner?.id) || status === AddonsStatusEnum.CANCELED}
          />
        )
      },
    },
    {
      title: <IntlMessages id="app.name" />,
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: (sortedInfo.columnKey === 'name' && sortedInfo.order) || null,
    },
    {
      title: <IntlMessages id="automationsForm.period" />,
      dataIndex: 'period',
      key: 'period',
      render: (period) => {
        return (
          <>
            <FormattedDate value={period[0]} format={dateFormat} /> - <FormattedDate value={period[1]} format={dateFormat} />
          </>
        )
      },
    },
    {
      title: <IntlMessages id="components.leavesColumns.duration" />,
      dataIndex: 'maximumDuration',
      key: 'maximumDuration',
      render: (maximumDuration, row: IAutomations) => {
        return <IntlMessages id="automations.leaveDurationLimit.duration" values={{ minimumDuration: row.minimumDuration, maximumDuration, units: row.units }} />
      },
    },
    {
      title: <IntlMessages id="app.created" />,
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      sortOrder: (sortedInfo.columnKey === 'createdAt' && sortedInfo.order) || null,
      render: (createdAt) => <FormattedDate value={createdAt.split('T')[0]} format={dateFormat} />,
    },
    {
      title: '',
      className: 'action',
      width: 78,
      dataIndex: 'id',
      key: 'id',
      render: (id: string, row: IAutomations) => (
        <Button
          type="link"
          disabled={(!amIAdmin && authUserId !== row.owner?.id) || status === AddonsStatusEnum.CANCELED}
          onClick={(event) => showConfirmDelete(row, event)}
          loading={Boolean(deleteAutomationId && deleteAutomationId === id)}
        >
          {!(deleteAutomationId && deleteAutomationId === id) && <DeleteOutlined />}
        </Button>
      ),
    },
  ]

  return (
    <div className="main-content addons-view-page">
      <PageHeader title={<IntlMessages id="automations.LEAVE_DURATION_LIMIT" />} parent={[{ label: <IntlMessages id="sidebar.addons" />, to: FrontendUrls.addons }]} />
      <div className="main-content-body">
        {amIAdmin && (
          <HandleSubscribeAutomations
            handleSubscribe={handleSubscribe}
            status={status}
            automationType="LEAVE_DURATION_LIMIT"
            formatMessage={formatMessage}
            includedInCurrentPlan={addonDetails?.includedIn?.includes(currentPlan ?? '') || false}
          />
        )}
        <div className="main-content-info" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" disabled={status === AddonsStatusEnum.CANCELED} onClick={createAutomation}>
            <IntlMessages id="automations.createPolicy" />
          </Button>
        </div>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            {automations.length > 0 ? (
              <Table
                dataSource={automations}
                columns={automationsColumns}
                className="clickable-table"
                onChange={(pagination, filters, sorter) => {
                  handleTableChange(pagination, filters, sorter as ITableSort)
                }}
                rowClassName={(record) => {
                  if (record.error) {
                    return 'clickable-table-error'
                  }
                  return ''
                }}
                onRow={(record) => {
                  if (record.error) {
                    return {
                      onClick: () => {
                        return
                      },
                    }
                  }
                  if (amIAdmin || authUserId === record.owner?.id) {
                    return {
                      onClick: () => history.push(`${FrontendUrls.addons}/${record.automationType.replace(/_/g, '-').toLowerCase()}/${record.id}/edit`),
                    }
                  } else {
                    return {
                      onClick: () => {
                        message.error(formatMessage({ id: 'automations.cantOpenAutomations' }) as string)
                      },
                    }
                  }
                }}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <EmptyData title={<IntlMessages id="automations.emptyViewTitle" />} subTitle={<IntlMessages id="automations.leaveDurationLimitEmptyViewTitle" />} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default LeaveDurationLimitView
